class GuideDrawer extends HTMLElement {
  constructor() {
    super();
    this.handleTriggerClick = this.handleTriggerClick.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleOverlayClick = this.handleOverlayClick.bind(this);
  }

  connectedCallback() {
    this.panels = Array.from(this.querySelectorAll('.guide-panel'));
    this.closeButtons = Array.from(
      this.querySelectorAll('.guide-drawer__close, .guide-panel__close')
    );

    // Every panel starts hidden; opening a specific guide reveals only that one.
    this.panels.forEach((panel) => {
      panel.hidden = true;
    });

    this.setAttribute('aria-hidden', 'true');
    this.previouslyFocusedElement = null;

    // Triggers live elsewhere on the page (e.g. the "Metal guide" link next
    // to a product option), so this listens at the document level rather
    // than inside the drawer itself.
    document.addEventListener('click', this.handleTriggerClick);

    this.closeButtons.forEach((button) => {
      button.addEventListener('click', () => this.close());
    });

    // Clicking the backdrop (the host element's own padding area) closes it.
    // If you add a dedicated .guide-drawer__overlay element, this still works
    // since it also listens on `this`.
    this.addEventListener('click', this.handleOverlayClick);

    this.wrapTables();
  }

  wrapTables() {
    this.querySelectorAll('table').forEach((table) => {
      if (table.parentElement?.classList.contains('table-wrapper')) return;

      const wrapper = document.createElement('div');
      wrapper.className = 'table-wrapper';
      table.replaceWith(wrapper);
      wrapper.appendChild(table);
    });
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.handleTriggerClick);
    document.removeEventListener('keydown', this.handleKeydown);
  }

  handleTriggerClick(event) {
    const trigger = event.target.closest('[data-guide-trigger]');
    if (!trigger) return;

    event.preventDefault();
    this.open(trigger.getAttribute('data-guide-trigger'), trigger);
  }

  handleOverlayClick(event) {
    const clickedInsideInner = event.target.closest('.guide-drawer__inner');
    if (!clickedInsideInner) {
      this.close();
    }
  }

  handleKeydown(event) {
    if (event.key === 'Escape') {
      this.close();
    }
  }

  open(guideId, triggerEl) {
    if (!guideId) return;

    const matchedPanel = this.panels.find(
      (panel) => panel.dataset.guideId === guideId
    );
    if (!matchedPanel) return;

    this.panels.forEach((panel) => {
      panel.hidden = panel !== matchedPanel;
    });

    this.previouslyFocusedElement = triggerEl || document.activeElement;

    this.classList.add('is-open');
    this.setAttribute('aria-hidden', 'false');
    document.body.classList.add('guide-drawer-open');

    document.addEventListener('keydown', this.handleKeydown);

    this.dispatchEvent(
      new CustomEvent('guide-drawer:open', {
        bubbles: true,
        detail: { guideId },
      })
    );
  }

  close() {
    if (!this.classList.contains('is-open')) return;

    this.classList.remove('is-open');
    this.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('guide-drawer-open');

    document.removeEventListener('keydown', this.handleKeydown);

    if (this.previouslyFocusedElement) {
      this.previouslyFocusedElement.focus();
    }

    this.dispatchEvent(new CustomEvent('guide-drawer:close', { bubbles: true }));
  }
}

if (!customElements.get('guide-drawer')) {
  customElements.define('guide-drawer', GuideDrawer);
}

class GuideOpener extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    this.guideId = this.dataset.id;

    if (!this.guideId || !this.button) return;

    const panel = document.querySelector(
      `.guide-panel[data-guide-id="${this.guideId}"]`
    );

    // No matching panel exists (e.g. guide was removed or unpublished) —
    // stay hidden rather than showing a button that does nothing.
    if (!panel) return;

    this.drawer = panel.closest('guide-drawer');

    this.button.addEventListener('click', (event) => {
      event.preventDefault();
      // Since this button also carries data-guide-trigger, guide-drawer's
      // document-level listener would otherwise catch this same click and
      // call open() a second time. Stop it here since we're calling open()
      // directly below.
      event.stopPropagation();

      const drawer = this.drawer || panel.closest('guide-drawer');

      if (!drawer) {
        console.warn('guide-opener: no <guide-drawer> element found on the page.');
        return;
      }

      if (typeof drawer.open !== 'function') {
        // Custom element script may not have loaded/upgraded yet.
        console.warn('guide-opener: guide-drawer has not initialized yet.');
        return;
      }

      drawer.open(this.guideId, this.button);
    });
  }
}

if (!customElements.get('guide-opener')) {
  customElements.define('guide-opener', GuideOpener);
}